import { initialize, webauthn } from '@transmitsecurity/platform-web-sdk';
import { environment } from '@rb-serve-app/core';

/**
 * Transmit Security (Mosaic) WebAuthn service for the web app.
 *
 * The SDK is initialized once (singleton promise) with the public client ID
 * and the Canada-region server path. Passkey authentication uses the native
 * browser modal, which handles the cross-device QR code (FIDO hybrid / caBLE)
 * flow itself — no QR library is needed.
 */

let initPromise: Promise<void> | null = null;

const START_AUTHENTICATION_FAILED = {
  errorCode: 'authentication_failed',
  message: 'Failed to start authentication',
} as const;

const PROBE_CHECK_FAILED = {
  errorCode: 'not_initialized',
  message: 'Passkey sign-in is not ready yet. Please try again in a moment.',
} as const;

/**
 * True when the browser supports WebAuthn at all. Intentionally NOT gated on
 * isPlatformAuthenticatorSupported(): that checks for biometrics on this
 * machine, which would wrongly hide the button on desktops that authenticate
 * via the phone QR code flow.
 */
export const isWebAuthnSupported = (): boolean =>
  typeof window !== 'undefined' && !!window.PublicKeyCredential;

/**
 * Initializes the Transmit SDK once. Safe to call multiple times; concurrent
 * and subsequent calls share the same promise. On failure, the promise is
 * cleared so a later call can retry.
 */
export const initializeTransmitSdk = (): Promise<void> => {
  if (!initPromise) {
    // initialize() is typed as void but may return a promise at runtime;
    // Promise.resolve() covers both.
    initPromise = Promise.resolve(
      initialize({
        clientId: environment.transmitSecurity.clientId,
        webauthn: {
          serverPath: environment.transmitSecurity.serverPath,
        },
      })
    ).catch((error: unknown) => {
      initPromise = null;
      throw error;
    });
  }
  return initPromise;
};

const getAuthenticateStartUrl = (): string => {
  const serverPath = environment.transmitSecurity.serverPath.replace(/\/$/, '');
  return `${serverPath}/cis/v1/auth/webauthn/authenticate/start`;
};

const normalizeUsername = (username: string): string =>
  String(username ?? '').trim().toLowerCase();

/**
 * Checks whether Transmit will start WebAuthn for this username without
 * opening a browser passkey sheet. A 200 means the user exists on Transmit.
 */
export const probeTransmitPasskeyUser = async (
  username: string
): Promise<void> => {
  const normalizedUsername = normalizeUsername(username);
  let response: Response;
  try {
    response = await fetch(getAuthenticateStartUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: environment.transmitSecurity.clientId,
        username: normalizedUsername,
      }),
    });
  } catch {
    throw { ...PROBE_CHECK_FAILED };
  }

  if (!response.ok) {
    throw { ...START_AUTHENTICATION_FAILED };
  }
};

/**
 * Silently confirms the username is registered with Transmit, then opens the
 * native modal. Username is passed to authenticate.modal only when
 * passUsername is true. Otherwise the modal stays discoverable so Chrome does
 * not show "No passkeys available" when this computer has no matching
 * credential.
 *
 * @returns Base64-encoded credential result to be sent to our backend.
 */
export const authenticateWithPasskey = async (
  username: string,
  passUsername = false
): Promise<string> => {
  const normalizedUsername = normalizeUsername(username);
  await initializeTransmitSdk();
  await probeTransmitPasskeyUser(normalizedUsername);
  return webauthn.authenticate.modal(
    passUsername ? { username: normalizedUsername } : {}
  );
};
